import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import { portalUserSelector } from '@/common/components/ecorr-store/portal-user/portal-user.selector';
import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { AppSettingsService } from '@/common/services/app-settings.service';
import { SurveyViewsInstanceBaseComponent } from '@/surveys/components/survey-views-instance-base/survey-views-instance-base.component';
import { SurveyViewsInstanceService } from '@/surveys/services/survey-views-instance.service';
import { FilingStatusDialogService } from '@/surveys/services/filing-status-dialog.service';
import { RequestExtensionDialogService } from '@/surveys/services/request-extension-dialog.service';
import { ShareSurveyAccessDialogService } from '@/surveys/services/share-survey-access-dialog.service';
import { EventService } from '@/surveys/services/event.service';
import { Component, Inject, Input, OnDestroy, OnInit, inject, signal, computed, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { SessionStorageService } from 'ngx-webstorage';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CalendarDialogService } from '@/surveys/services/calendar-dialog.service';
import { WINDOW } from '@/window';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { DecodeXmlModule } from '@/surveys/pipe/decode-xml/decode-xml.module';
import { ByPassSecurityModule } from '@/surveys/pipe/by-pass-security/by-pass-security.module';

@Component({
    selector: 'app-grid-view-instance',
    templateUrl: './grid-view-instance.component.html',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatInputModule,
        MatIconModule,
        MatListModule,
        MatButtonModule,
        MatCardModule,
        MatTooltipModule,
        MatFormFieldModule,
        MatMenuModule,
        MatDividerModule,
        DecodeXmlModule,
        ByPassSecurityModule
    ],
    providers: [
        ParadataEventService,
        RequestExtensionDialogService,
        ShareSurveyAccessDialogService,
        FilingStatusDialogService,
        SurveyViewsInstanceService,
        CalendarDialogService,
        EventService,
        SessionStorageService
    ]
})
export class GridViewInstanceComponent extends SurveyViewsInstanceBaseComponent implements OnInit, OnDestroy {
    // Services using inject()
    private destroyRef = inject(DestroyRef);
    public router = inject(Router);
    public route = inject(ActivatedRoute);
    public store = inject(Store<PortalUserState>);
    public requestExtensionDialogService = inject(RequestExtensionDialogService);
    public shareSurveyAccessDialogService = inject(ShareSurveyAccessDialogService);
    public paradataEventService = inject(ParadataEventService);
    public filingStatusDialogService = inject(FilingStatusDialogService);
    public surveyViewsInstanceService = inject(SurveyViewsInstanceService);
    public calendarDialogService = inject(CalendarDialogService);
    public eventService = inject(EventService);
    public appSettingsService = inject(AppSettingsService);
    public sessionStorageService = inject(SessionStorageService);
    public window = inject(WINDOW);

    // Signals for reactive state
    user = signal<PortalUser | null>(null);
    configError = signal<boolean>(false);

    // Computed signals
    currentUser = computed(() => this.user());
    hasConfigError = computed(() => this.configError());
    currentSettings = computed(() => this.settings);

    // Legacy subscriptions for compatibility
    user$: Subscription;

    @Input('card-id')
    cardId: string;

    constructor() {
        super(
            inject(Router),
            inject(ActivatedRoute),
            inject(RequestExtensionDialogService),
            inject(ShareSurveyAccessDialogService),
            inject(ParadataEventService),
            inject(FilingStatusDialogService),
            inject(SurveyViewsInstanceService),
            inject(EventService),
            inject(AppSettingsService),
            inject(SessionStorageService),
            inject(WINDOW)
        );
        this.user$ = this.store.select(portalUserSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                user => {
                    this.user.set(user);
                }
            );
    }

    ngOnInit(): void {
        this.showOnlyMessage = this.survey.preCanvasFlag;
        this.surveyAdded();
    }

    exportSurvey() {
        this.calendarDialogService.open(this.survey.surveyInstanceId);
    }

    ngOnDestroy() {
        this.paradataEventService.flushParadataevents();
        // Subscriptions are now handled by takeUntilDestroyed, no manual unsubscribe needed here.
        super.destroy();
    }
    
}
